import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", status: "initiated" });
  const [attempts, setAttempts] = useState(0);
  const timer = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setStatus(res.data);
        if (res.data.payment_status === "paid") {
          clearInterval(timer.current);
        }
      } catch (e) { /* ignore polling errors */ }
      setAttempts(a => a + 1);
    };
    poll();
    timer.current = setInterval(poll, 2000);
    // stop after 30 tries (~60s)
    const stop = setTimeout(() => clearInterval(timer.current), 60000);
    return () => { clearInterval(timer.current); clearTimeout(stop); };
  }, [sessionId]);

  const paid = status.payment_status === "paid";

  return (
    <div className="max-w-2xl mx-auto p-16 text-center" data-testid="payment-success-page">
      {paid ? (
        <>
          <CheckCircle size={72} weight="fill" className="text-[#D4AF37] mx-auto" />
          <h1 className="mt-6 font-serif-display text-4xl text-white">Payment received.</h1>
          <p className="mt-4 text-zinc-400">Your booking is confirmed and we're routing it to a provider now.</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => nav("/dashboard")}
              data-testid="go-to-dashboard-btn"
              className="px-6 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold"
            >
              View bookings
            </button>
            <Link to="/" className="px-6 py-3 rounded-full border border-white/20 text-sm">Home</Link>
          </div>
        </>
      ) : (
        <>
          <CircleNotch size={72} weight="bold" className="text-[#D4AF37] mx-auto animate-spin" />
          <h1 className="mt-6 font-serif-display text-3xl text-white">Confirming payment…</h1>
          <p className="mt-4 text-zinc-500 text-sm">Attempt {attempts}. This usually takes a moment.</p>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-2xl mx-auto p-16 text-center" data-testid="payment-cancel-page">
      <XCircle size={72} weight="fill" className="text-red-400 mx-auto" />
      <h1 className="mt-6 font-serif-display text-4xl text-white">Payment cancelled.</h1>
      <p className="mt-4 text-zinc-400">No charge was made. Restart booking anytime.</p>
      <Link to="/" className="mt-10 inline-block px-6 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold">Back to home</Link>
    </div>
  );
}
